import ProcoreAPI from 'api/ProcoreAPI';
import { AxiosResponse } from 'axios';
import { useEffect, useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthStore from 'store/useAuthStore';
import { catchErrorMessage } from 'utility/helpers';
import { shallow } from 'zustand/shallow';

export function useProcoreAuth() {
  const navigate = useNavigate();
  const [setAccessToken, setRefreshToken, setUser] = useAuthStore(
    (state) => [state.setAccessToken, state.setRefreshToken, state.setUser],
    shallow,
  );
  const [isProcoreLoading, setProcoreLoading] = useState(false);
  const procoreAuth = useMutation<AxiosResponse<any>, Error, string>(
    (code) => ProcoreAPI.loginProcore(code),
    {
      onError: (error) => {
        toast.error(catchErrorMessage(error));
      },
      onSuccess: ({ data: response }) => {
        setTimeout(() => {
          setAccessToken(response.auth_token);
          setRefreshToken(response.refresh_token);

          const isNovaAdmin = !!response?.roles?.find(
            (role) => role.roleName === 'ROLE_NOVA_ADMIN',
          );
          setUser({
            email: response.email,
            companyId: response.companyId,
            isNovaAdmin,
            isAssociatedWithOtherCompanies:
              !isNovaAdmin && response?.associatedCompanies?.includes(response.companyId)
                ? response?.associatedCompanies?.length > 1
                : response?.associatedCompanies?.length > 0,
          });
        }, 1);
      },
    },
  );
  const { mutate: loginWithProcore } = procoreAuth;

  useEffect(() => {
    window.addEventListener('storage', checkProcoreCode);
    return () => {
      window.removeEventListener('storage', checkProcoreCode);
    };
  }, []);

  const checkProcoreCode = useCallback(() => {
    const code = localStorage.getItem('procoreCode');
    if (!!code) {
      loginWithProcore(code);
      localStorage.removeItem('procoreCode');
    }
  }, []);

  const continueWithProcore = useCallback(async () => {
    try {
      setProcoreLoading(true);
      const result = await ProcoreAPI.createProcoreUrl();
      if (result.data) {
        const requiredPopupWidth = 600;
        const requiredPopupHeight = 700;
        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

        const width = window.innerWidth
          ? window.innerWidth
          : document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : screen.width;
        const height = window.innerHeight
          ? window.innerHeight
          : document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : screen.height;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - requiredPopupWidth) / 2 / systemZoom + dualScreenLeft;
        const top = (height - requiredPopupHeight) / 2 / systemZoom + dualScreenTop;
        window.open(
          result.data,
          '',
          `
          scrollbars=yes,
          width=${requiredPopupWidth / systemZoom}, 
          height=${requiredPopupHeight / systemZoom}, 
          top=${top}, 
          left=${left}
        `,
        );
      }
    } catch (error) {
      console.error('CONTINUE_WITH_PROCORE_ERROR', error);
    } finally {
      setProcoreLoading(false);
    }
  }, []);

  return {
    ...procoreAuth,
    isProcoreLoading,
    continueWithProcore,
  };
}
